import { api } from './client';
import type {
  Project,
  ProjectWithStats,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectType,
  ProjectStatus,
} from '@affogato/shared';

// Response types
interface ProjectsListResponse {
  projects: ProjectWithStats[];
}

interface ProjectResponse {
  project: ProjectWithStats;
}

interface ProjectDetailResponse {
  project: ProjectWithStats & {
    client: {
      id: string;
      name: string;
      company: string | null;
    };
    folders: Array<{
      id: string;
      name: string;
      path: string;
      color: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  };
}

// Query params for listing projects
interface ProjectsListParams {
  clientId?: string;
  status?: ProjectStatus;
}

// Projects API
export const projectsApi = {
  list: (params?: ProjectsListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.clientId) searchParams.set('clientId', params.clientId);
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    return api.get<ProjectsListResponse>(`/projects${query ? `?${query}` : ''}`);
  },

  get: (id: string) => api.get<ProjectDetailResponse>(`/projects/${id}`),

  create: (data: CreateProjectRequest) =>
    api.post<ProjectResponse>('/projects', data),

  update: (id: string, data: UpdateProjectRequest) =>
    api.patch<ProjectResponse>(`/projects/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/projects/${id}`),

  togglePortalVisibility: (id: string, visible: boolean) =>
    api.patch<{ success: boolean; portalVisible: boolean }>(
      `/projects/${id}/portal-visibility`,
      { visible }
    ),
};

// Re-export types for convenience
export type { Project, ProjectWithStats, CreateProjectRequest, UpdateProjectRequest, ProjectType, ProjectStatus };
